import { useCallback, useEffect, useState } from 'react';
import { stateSetter } from '@/utils';
import {
	AdministrationLogsReturnType,
	IAdministrationLog,
	IAdministrationLogQuery
} from '../use-administration-logs/types';
import { fetchUserLogs } from './actions';

// Istoric utilizator (GET api/users/:id/logs) — aceeași formă ca istoricul de administrație, paginat.
// Fallback stabil ca identitate pentru `data` neîncărcat — un `[]` nou la fiecare
// randare re-declanșează în lanț efectele consumatorilor cu dependență pe `data`
// („Maximum update depth exceeded").
const EMPTY_LIST: never[] = [];

export function useUserLogs<T extends IAdministrationLog | IAdministrationLog[]>(
	userId: number | string | undefined = undefined,
	queryParam: IAdministrationLogQuery | undefined = undefined
): AdministrationLogsReturnType<T> {
	const [data, setData] = useState<T | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [page, setPage] = useState<number>(0);
	const [items, setItems] = useState<number>(10);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(1);

	const handleResponse = (pageParam: number, itemsParam: number): void => {
		setTotalPages(pageParam);
		setTotalItems(itemsParam);
	};

	const getLogs = useCallback(
		async (
			id: number | string,
			pageParam: number,
			itemsParam: number,
			query?: IAdministrationLogQuery
		) => {
			try {
				setLoading(true);
				const response = await fetchUserLogs(id, pageParam, itemsParam, query, handleResponse);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		[setData]
	);

	useEffect(() => {
		if (userId === undefined) {
			setLoading(false);
			return;
		}

		getLogs(userId, page, items, queryParam);
	}, [userId, queryParam, page, items, getLogs]);

	return {
		data: data || (EMPTY_LIST as IAdministrationLog[] as T),
		setData: setData as stateSetter<T>,
		getLogs,
		loading,
		items,
		page,
		setItems,
		setPage,
		totalPages,
		totalItems
	};
}
